// tabs/ThinkyBaaSAnalytics.jsx
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartLine, faCheckCircle, faFileAlt, faPlay } from "@fortawesome/free-solid-svg-icons";

const dummyReports = [
  {
    id: 1,
    name: "Financial Analytics Report",
    description: "Summarizes transaction volumes and smart contract performance.",
    generated: false,
    analyzed: false,
    verified: false,
    log: [],
    precondition: true, // simulate data availability
  },
  {
    id: 2,
    name: "Supply Chain Report",
    description: "Tracks shipment milestones, delays, and compliance across blockchain.",
    generated: false,
    analyzed: false,
    verified: false,
    log: [],
    precondition: false, // simulate missing data
  },
  {
    id: 3,
    name: "Education Certificates Audit",
    description: "Analyzes student completion rates and certificate issuance.",
    generated: false,
    analyzed: false,
    verified: false,
    log: [],
    precondition: true,
  },
];

const ThinkyBaaSAnalytics = () => {
  const [reports, setReports] = useState(dummyReports.map(r => ({ ...r, log: r.log || [] })));

  // Generate report
  const generateReport = (id) => {
    setReports(prev =>
      prev.map(r =>
        r.id === id && !r.generated
          ? { ...r, generated: true, log: [...(r.log || []), { event: "Report Generated", time: new Date().toLocaleTimeString() }] }
          : r
      )
    );
  };

  // Analyze report
  const analyzeReport = (id) => {
    setReports(prev =>
      prev.map(r => {
        if (r.id === id) {
          if (!r.precondition) {
            return { ...r, log: [...(r.log || []), { event: "Analysis blocked: Data not ready", time: new Date().toLocaleTimeString() }] };
          }
          return { ...r, analyzed: true, log: [...(r.log || []), { event: "Report Analyzed", time: new Date().toLocaleTimeString() }] };
        }
        return r;
      })
    );
  };

  // Verify report
  const verifyReport = (id) => {
    setReports(prev =>
      prev.map(r =>
        r.id === id && r.analyzed && !r.verified
          ? { ...r, verified: true, log: [...(r.log || []), { event: "Insights Verified", time: new Date().toLocaleTimeString() }] }
          : r
      )
    );
  };

  return (
    <div className="bg-gray-900 p-6 rounded-2xl shadow-lg space-y-6">
      <div className="flex items-center mb-4">
        <FontAwesomeIcon icon={faChartLine} className="text-blue-400 text-xl lg:text-2xl mr-4" />
        <h2 className="text-lg lg:text-xl font-bold">Analytics & Reporting Workflow</h2>
      </div>

      <p className="text-gray-300 text-sm">
        This page simulates a blockchain <span className="font-semibold">Analytics & Reporting workflow</span>:
        Generate → Analyze → Verify. Each report tracks its progress in the event log below.
      </p>

      <div className="space-y-4">
        {reports.map(r => (
          <div key={r.id} className="bg-gray-800 p-4 rounded-xl border border-gray-700">
            <h3 className="text-xl font-semibold mb-1">{r.name}</h3>
            <p className="text-gray-400 mb-2">{r.description}</p>

            <div className="flex flex-wrap gap-2 mb-2">
              <span className={`px-2 py-1 rounded-sm text-sm ${r.generated ? "bg-green-600" : "bg-yellow-600"}`}>
                {r.generated ? "Generated ✅" : "Pending Generation ⏳"}
              </span>
              <span className={`px-2 py-1 rounded-sm text-sm ${r.analyzed ? "bg-green-600" : "bg-yellow-600"}`}>
                {r.analyzed ? "Analyzed ✅" : "Pending Analysis ⏳"}
              </span>
              <span className={`px-2 py-1 rounded-sm text-sm ${r.verified ? "bg-green-600" : "bg-yellow-600"}`}>
                {r.verified ? "Verified ✅" : "Pending Verification ⏳"}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                disabled={r.generated}
                onClick={() => generateReport(r.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  r.generated ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faFileAlt} className="mr-1" /> Generate
              </button>
              <button
                disabled={!r.generated || r.analyzed}
                onClick={() => analyzeReport(r.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  !r.generated || r.analyzed ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faPlay} className="mr-1" /> Analyze
              </button>
              <button
                disabled={!r.analyzed || r.verified}
                onClick={() => verifyReport(r.id)}
                className={`w-32 text-sm px-3 py-1 rounded-lg font-semibold transition ${
                  !r.analyzed || r.verified ? "bg-gray-600 cursor-not-allowed" : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                <FontAwesomeIcon icon={faCheckCircle} className="mr-1" /> Verify
              </button>
            </div>

            <div className="mt-3 bg-gray-900 p-2 rounded-lg border border-gray-700">
              <h4 className="text-gray-300 font-semibold mb-1">Event Log:</h4>
              {(!r.log || r.log.length === 0) ? (
                <p className="text-gray-500 text-sm">No events yet.</p>
              ) : (
                <ul className="text-gray-400 text-sm list-disc list-inside space-y-1">
                  {r.log?.map((log, idx) => (
                    <li key={idx}>
                      [{log.time}] {log.event}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ThinkyBaaSAnalytics;
